/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { CdkScrollable } from '@angular/cdk/scrolling';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogContent, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { PdfPrintResult } from '@map/command/print/state/print-options.model';
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { DeviceService } from '@shared/services/device.service';
import { saveAs } from 'file-saver';

export interface PdfData {
    pdfBlob: Blob | string;
    isBlob: boolean;
}

@Component({
    selector: 'dash-gis-print-pdf-dialog',
    templateUrl: './printPdfDialog.html',
    styleUrls: [ './printPdfDialog.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        FlexDirective,
        LayoutDirective,
        LayoutAlignDirective,
        CdkScrollable,
        MatDialogContent,
        MatDialogActions,
        MatButton
    ]
})
export class PdfDialogComponent implements AfterViewInit {
    constructor(
        private dialogRef: MatDialogRef<PdfDialogComponent>,
        private deviceService: DeviceService,
        private sanitizer: DomSanitizer,
        @Inject(MAT_DIALOG_DATA) private pdfData: PdfPrintResult
    ) {}

    @ViewChild('objectElement', { read: ElementRef })
    private objectElement: ElementRef;

    ngAfterViewInit(): void {
        if (
            this.isIOS ||
            (!this.isFirefox &&
                this.objectElement &&
                !(this.objectElement.nativeElement as HTMLObjectElement).contentDocument)
        ) {
            this.dialogRef.updateSize();
        }
    }

    doDownload() {
        saveAs(this.pdfData.blob, this.pdfData.filename);
    }

    close() {
        this.dialogRef.close();
    }

    private blobToBlobUri(blob: Blob): string {
        return URL.createObjectURL(blob);
    }

    get isIOS() {
        return this.deviceService.isIOS;
    }

    get isFirefox() {
        return this.deviceService.isFirefox;
    }

    get blobUri() {
        const uri = this.pdfData.isBlob ? this.blobToBlobUri(this.pdfData.blob as Blob) : this.pdfData.blob;
        return this.sanitizer.bypassSecurityTrustResourceUrl(uri as string);
    }
}
